import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from './AuthContext';
import { SalePageSubscription } from '../types/salepage';

interface SalePageContextType {
  subscription: SalePageSubscription | null;
  loading: boolean;
  hasAccess: boolean;
  isTrialActive: boolean;
  daysRemaining: number;
  isSubscriptionExpired: () => boolean;
  initializeTrial: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const SalePageContext = createContext<SalePageContextType | undefined>(undefined);

export const useSalePage = () => {
  const context = useContext(SalePageContext);
  if (!context) {
    throw new Error('useSalePage must be used within SalePageProvider');
  }
  return context;
};

export const SalePageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthContext();
  const [subscription, setSubscription] = useState<SalePageSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const CACHE_DURATION = 30000; // 30 seconds cache

  const calculateDaysRemaining = (endDate: Timestamp | null): number => {
    if (!endDate) return 0;
    const now = new Date();
    const end = endDate.toDate();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const isSubscriptionExpired = (): boolean => {
    if (!subscription) return true;
    
    if (subscription.status === 'expired' || subscription.status === 'cancelled') {
      return true;
    }
    
    if (subscription.subscriptionEndDate) {
      const now = new Date();
      const endDate = subscription.subscriptionEndDate.toDate();
      return now > endDate;
    }
    
    if (subscription.status === 'trial' && subscription.trialEndDate) {
      const now = new Date();
      const endDate = subscription.trialEndDate.toDate();
      return now > endDate;
    }
    
    return false;
  };

  const hasAccess = subscription && !isSubscriptionExpired() && (subscription.status === 'trial' || subscription.status === 'active');
  const isTrialActive = subscription?.status === 'trial' && calculateDaysRemaining(subscription.trialEndDate || null) > 0;
  const daysRemaining = calculateDaysRemaining(subscription?.trialEndDate || null);

  const fetchSubscription = async (forceRefresh: boolean = false) => {
    console.log('[SalePageContext] fetchSubscription called', { userId: user?.uid, forceRefresh });
    
    if (!user?.uid) {
      console.log('[SalePageContext] No user, setting loading to false');
      setLoading(false);
      return;
    }

    // Cache check - skip if data is fresh and not forced
    const now = Date.now();
    if (!forceRefresh && lastFetchTime > 0 && (now - lastFetchTime) < CACHE_DURATION) {
      console.log('[SalePageContext] Using cached data');
      setLoading(false);
      return;
    }

    console.log('[SalePageContext] Fetching subscription data...');
    
    // Add timeout protection
    const timeoutId = setTimeout(() => {
      console.error('[SalePageContext] Fetch timeout - forcing loading to false');
      setLoading(false);
    }, 10000); // 10 second timeout
    
    try {
      // Fetch both documents in parallel for better performance
      const userDocRef = doc(db, 'users', user.uid);
      const salePageDocRef = doc(db, 'salePageSubscriptions', user.uid);
      
      const [userDocSnap, docSnap] = await Promise.all([
        getDoc(userDocRef),
        getDoc(salePageDocRef)
      ]);
      
      let currentUserType = null;
      let actualUsername = '';

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        currentUserType = userData.userType || null;
        actualUsername = userData.username || userData.displayName?.toLowerCase().replace(/[^a-z0-9_-]/g, '') || '';
      }

      if (docSnap.exists()) {
        const subData = docSnap.data() as SalePageSubscription;
        
        // Lazy assignment: Update userType when Salepage subscription exists
        if (subData.status === 'active' || subData.status === 'trial') {
          const hasTeacherSubscription = userDocSnap.exists() && userDocSnap.data().subscriptionTier && userDocSnap.data().subscriptionTier !== 'free';
          const newUserType = hasTeacherSubscription ? 'both' : 'salepage';
          
          if (currentUserType !== newUserType) {
            await updateDoc(userDocRef, { userType: newUserType });
            console.log('[SalePageContext] Updated userType to:', newUserType);
          }
        }
        
        // Update username if it's different from actual username
        let needsUpdate = false;
        let updatedSubData = { ...subData };
        
        if (actualUsername && subData.username !== actualUsername) {
          updatedSubData.username = actualUsername;
          needsUpdate = true;
          console.log('[SalePageContext] Updating username from', subData.username, 'to', actualUsername);
        }
        
        if (needsUpdate) {
          await setDoc(salePageDocRef, updatedSubData);
          setSubscription(updatedSubData);
        } else {
          setSubscription(subData);
        }
        setLastFetchTime(now);
      } else {
        // Fallback: some older flows may store salePageSubscriptions with a different document id.
        try {
          const q = query(collection(db, 'salePageSubscriptions'), where('userId', '==', user.uid));
          const qs = await getDocs(q);
          if (!qs.empty) {
            const subData = qs.docs[0].data() as SalePageSubscription;
            // Migrate to canonical document id for consistent reads across the app
            await setDoc(salePageDocRef, { ...subData, userId: user.uid }, { merge: true });
            setSubscription(subData);
            setLastFetchTime(now);
            return;
          }
        } catch (error) {
          console.error('Error querying sale page subscription fallback:', error);
        }

        // No Pro+ Yearly auto-grant anymore — new users have no Sale Page subscription (test mode)
        setSubscription(null);
        setLastFetchTime(now);
      }
    } catch (error) {
      console.error('[SalePageContext] Error fetching sale page subscription:', error);
      // Set subscription to null on error to prevent infinite loading
      setSubscription(null);
      setLastFetchTime(now);
    } finally {
      clearTimeout(timeoutId);
      console.log('[SalePageContext] Setting loading to false');
      setLoading(false);
    }
  };

  const initializeTrial = async () => {
    if (!user?.uid || !user?.email) {
      throw new Error('User must be logged in');
    }

    if (subscription?.isTrialUsed) {
      throw new Error('Trial already used');
    }

    const now = Timestamp.now();
    const trialEnd = Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // 7 days

    const newSubscription: SalePageSubscription = {
      userId: user.uid,
      email: user.email,
      status: 'trial',
      plan: 'free',
      trialStartDate: now,
      trialEndDate: trialEnd,
      isTrialUsed: true,
      subscriptionStartDate: null,
      subscriptionEndDate: null,
      billingPeriod: null,
      hasPathUrl: false,
      hasSubdomain: false,
      username: null,
      lastPaymentDate: null,
      lastPaymentAmount: null,
      createdAt: now,
      updatedAt: now,
    };

    try {
      const docRef = doc(db, 'salePageSubscriptions', user.uid);
      await setDoc(docRef, newSubscription);
      setSubscription(newSubscription);
    } catch (error) {
      console.error('Error initializing trial:', error);
      throw error;
    }
  };

  const refreshSubscription = async () => {
    await fetchSubscription(true); // Force refresh
  };

  useEffect(() => {
    console.log('[SalePageContext] useEffect triggered', { userId: user?.uid });
    if (user?.uid) {
      fetchSubscription();
    } else {
      console.log('[SalePageContext] No user in useEffect, setting loading to false');
      setLoading(false);
    }
  }, [user?.uid]);

  // Check trial expiry (for both 'trial' and 'pending_email_verification')
  useEffect(() => {
    if ((subscription?.status === 'trial' || subscription?.status === 'pending_email_verification') && subscription.trialEndDate) {
      const checkExpiry = () => {
        const remaining = calculateDaysRemaining(subscription.trialEndDate);
        if (remaining <= 0) {
          // Trial expired - update status
          const updateStatus = async () => {
            try {
              const docRef = doc(db, 'salePageSubscriptions', user!.uid);
              await setDoc(docRef, {
                ...subscription,
                status: 'expired',
                updatedAt: Timestamp.now(),
              });
              await refreshSubscription();
            } catch (error) {
              console.error('Error updating expired trial:', error);
            }
          };
          updateStatus();
        }
      };

      checkExpiry();
      const interval = setInterval(checkExpiry, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [subscription?.status, subscription?.trialEndDate]);

  return (
    <SalePageContext.Provider
      value={{
        subscription,
        loading,
        hasAccess,
        isTrialActive,
        daysRemaining,
        isSubscriptionExpired,
        initializeTrial,
        refreshSubscription,
      }}
    >
      {children}
    </SalePageContext.Provider>
  );
};
